<script setup lang="ts">
import { toRef } from "vue";
import {
  useGetDrawingBranches,
  // useSwitchDrawingBranch,
} from "@/stores/queries/drawings.query";
import DRUserAvatar from "@/components/UI/DRUserAvatar.vue";
import { useDates } from "@/composable/useDates";
import type { DRDrawing } from "@/types";
import DrawingBranchActions from "./DrawingBranchActions.vue";

const props = defineProps<{
  drawing: DRDrawing;
}>();

/*const emits = defineEmits<{
  (e: "switched-branch"): void;
}>();*/

const drawingId = toRef(() => props.drawing.id);
const { data: branches, isLoading, isError } = useGetDrawingBranches(drawingId);
const { formatDistanceToNow } = useDates();

const isCurrentBranch = (branchId: number) => {
  return props.drawing.lastRevision.branchId === branchId;
};
</script>

<template>
  <div>
    <div v-if="isLoading" class="loading">
      {{ $t("dashboard:branches.loading") }}
    </div>
    <div v-else-if="isError" class="error">
      {{ $t("dashboard:branches.error") }}
    </div>
    <div v-else-if="!branches || branches.length === 0" class="empty">
      {{ $t("dashboard:branches.empty") }}
    </div>
    <div v-else>
      <div
        style="padding-left: 60px"
        v-for="(branch, index) in branches"
        :key="branch.id"
      >
        <div
          class="py-2 d-flex align-items-center justify-content-between"
          :class="{
            'border-top': !!index,
          }"
        >
          <div class="flex-grow-1">
            <div class="d-flex align-items-center gap-2">
              <el-icon><i class="bi-diagram-2"></i></el-icon>
              <div class="col-12 text-truncate">
                {{ branch.name }}
              </div>
            </div>
            <div
              class="d-flex align-items-center gap-2 text-grey"
              style="font-size: 10px"
            >
              <span
                >{{ $t("edited") }}{{ formatDistanceToNow(branch.updatedAt) }}
              </span>

              <div class="d-flex align-items-center gap-1">
                <DRUserAvatar
                  v-for="contributor in branch.contributors"
                  :key="contributor.id"
                  :size="13"
                  :user="contributor"
                  :tooltip="contributor.full_name"
                />
              </div>

              <span class="contributors-label">{{
                $t("dashboard:branches.contributors", {
                  count: branch.contributors.length,
                })
              }}</span>
            </div>
          </div>

          <div class="me-3 d-flex align-items-center gap-2">
            <span
              v-if="isCurrentBranch(branch.id)"
              class="current-branch-badge"
            >
              {{ $t("dashboard:branches.current") }}
            </span>
            <DrawingBranchActions
              :drawing-id="drawing.id"
              :drawing-name="drawing.name"
              :branch="branch"
              :is-current="isCurrentBranch(branch.id)"
              @switched-branch="$emit('switched-branch')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.current-branch-badge {
  padding: 0.125rem 0.5rem;
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 12px;
  background-color: #e3f2fd;
  color: #1976d2;
  white-space: nowrap;
}

.loading,
.error,
.empty {
  padding: 1rem;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
}

.error {
  color: #d32f2f;
}
</style>
